import { ChecklistItemRecord } from "models/view-models/block-configurations/checklist-item-record";
interface UseChecklistItemKeyPresses {
    handleKeyDown: (
        e: React.KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>,
        index: number
    ) => void;
}

const useChecklistItemKeyPresses = (
    item: ChecklistItemRecord,
    onSplitItem: (index: number, cursorPosition: number) => void,
    onMergeWithPreviousItem: (index: number) => void,
    onMergeWithNextItem: (index: number) => void,
    onItemTextChanged: (newText: string) => void
): UseChecklistItemKeyPresses => {
    const handleOnEnter = (
        index: number,
        cursorPosition: number,
        isShiftEnterMode: boolean
    ) => {
        if (isShiftEnterMode) {
            handleAddNewLineWithinItem(index, cursorPosition);
        } else {
            onSplitItem(index, cursorPosition);
        }
    };

    const handleAddNewLineWithinItem = async (
        index: number,
        cursorPosition: number
    ) => {
        const textBeforeCursor = item.text.slice(0, cursorPosition);
        const textAfterCursor = item.text.slice(cursorPosition);

        onItemTextChanged(textBeforeCursor + "\n" + textAfterCursor);

        //move the cursor to the new line after the update
        setTimeout(() => {
            const element = document.getElementById(
                `item-text-${index}`
            ) as HTMLInputElement | null;
            element?.focus();
            element?.setSelectionRange(cursorPosition + 1, cursorPosition + 1);
        }, 50);
    };

    const handleKeyDown = (
        e: React.KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>,
        index: number
    ) => {
        if (e.shiftKey && e.key === "Enter") {
            //Let the AutoResizeRichText text handle this natively
            return;
        }

        if (e.key === "Enter") {
            e.preventDefault();
            handleOnEnter(
                index,
                e.currentTarget.selectionStart ?? 0,
                e.shiftKey
            );
        }
        if (e.key === "Backspace") {
            const currentInput = e.currentTarget;

            const selectionLength =
                (currentInput.selectionEnd ?? 0) -
                (currentInput.selectionStart ?? 0);

            if (selectionLength > 0) {
                return;
            }

            const shouldMergeCurrentItemWithPrevious = (
                cursorPosition: number
            ) => cursorPosition === 0 && index > 0;
            if (
                shouldMergeCurrentItemWithPrevious(
                    currentInput.selectionStart ?? 0
                )
            ) {
                e.preventDefault();
                onMergeWithPreviousItem(index);
            }
        }

        if (e.key === "Delete") {
            const currentInput = e.currentTarget;

            const shouldMergeCurrentItemWithNext = (
                currentInput: HTMLInputElement | HTMLTextAreaElement
            ) => {
                const cursorPosition = currentInput.selectionStart ?? 0;
                const isNotHighlightedSection =
                    currentInput.selectionStart === currentInput.selectionEnd;
                const isAtEndOfInput =
                    cursorPosition === (currentInput.value?.length ?? 0);
                return isNotHighlightedSection && isAtEndOfInput;
            };

            if (shouldMergeCurrentItemWithNext(currentInput)) {
                e.preventDefault();
                onMergeWithNextItem(index);
            }
        }
    };

    return {
        handleKeyDown,
    };
};

export default useChecklistItemKeyPresses;
